/**
 * Loads every published Admin blog that has posts.
 * Sources run in parallel: Storefront blogs query and the blog sitemap.
 * (/blogs.json is Admin-only and 404s on the storefront — do not call it.)
 */
const BLOG_PATH = /\/blogs\/([^/?#]+)(?:\/|$)/i;
const BLOGS_QUERY = `{ blogs(first: 50) { nodes { handle title onlineStoreUrl } } }`;

function blogPathHandle(value) {
  try {
    const path = String(value || '').startsWith('http') ? new URL(value).pathname : String(value || '');
    const match = path.match(BLOG_PATH);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

function titleFromHandle(handle) {
  return String(handle || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function isBrandBlog(handle, title, shopHandle) {
  const handleKey = String(handle || '').toLowerCase();
  const titleKey = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const shopKey = String(shopHandle || '').toLowerCase();
  if (!handleKey) return true;
  if (handleKey.includes('parajohn') || handleKey === 'para-john') return true;
  if (titleKey.includes('parajohn') || titleKey === 'para-john') return true;
  if (shopKey && (handleKey === shopKey || titleKey === shopKey)) return true;
  return false;
}

function travelIconMarkup() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.15" y="8.1" width="3.3" height="6.15" rx="0.45" stroke="currentColor" stroke-width="1.15"/>
    <rect x="5.25" y="5.15" width="3.5" height="9.1" rx="0.45" stroke="currentColor" stroke-width="1.15"/>
    <path d="M6.55 5.15v-1.05a.7.7 0 0 1 .7-.7h.55a.7.7 0 0 1 .7.7v1.05" stroke="currentColor" stroke-width="1.15"/>
    <rect x="9.85" y="3.1" width="4.4" height="11.15" rx="0.5" stroke="currentColor" stroke-width="1.15"/>
    <path d="M11.5 3.1V2.05a.7.7 0 0 1 .7-.7h.55a.7.7 0 0 1 .7.7V3.1" stroke="currentColor" stroke-width="1.15"/>
  </svg>`;
}

function luggageIconMarkup() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="3.15" y="4.2" width="9.7" height="10.05" rx="1" stroke="currentColor" stroke-width="1.15"/>
    <path d="M6.2 4.2V3.15a1.8 1.8 0 0 1 3.6 0V4.2" stroke="currentColor" stroke-width="1.15"/>
    <path d="M6.15 6.55v5.35M8 6.55v5.35M9.85 6.55v5.35" stroke="currentColor" stroke-width="1.15"/>
    <path d="M5.1 14.25v.9M10.9 14.25v.9" stroke="currentColor" stroke-width="1.15" stroke-linecap="round"/>
  </svg>`;
}

function typeIconMarkup(title) {
  const key = String(title || '').toLowerCase();
  if (key.includes('travel')) return travelIconMarkup();
  return luggageIconMarkup();
}

function createTypePill({ handle, title, url, active, iconHtml }) {
  const item = document.createElement('li');
  item.className = 'blog-categories__item';
  item.dataset.blogHandle = handle;

  const link = document.createElement('a');
  link.className = `blog-categories__pill${active ? ' blog-categories__pill--active' : ''}`;
  link.href = url;
  if (active) link.setAttribute('aria-current', 'page');

  const icon = document.createElement('span');
  icon.className = 'blog-categories__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = iconHtml || typeIconMarkup(title);

  const label = document.createElement('span');
  label.className = 'blog-categories__label';
  label.textContent = title;

  link.append(icon, label);
  item.append(link);
  return item;
}

function toSameOrigin(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${window.location.origin}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

async function fetchText(url) {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return '';
    return response.text();
  } catch {
    return '';
  }
}

async function fetchJson(url, options) {
  try {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

function parseLocs(xml) {
  return [...String(xml).matchAll(/<loc>\s*([^<]+?)\s*<\/loc>/gi)].map((match) => match[1].trim());
}

function upsert(map, { handle, title, url, hasPosts }) {
  if (!handle) return;
  const current = map.get(handle) || { handle, title: '', url: `/blogs/${encodeURIComponent(handle)}`, hasPosts: false };
  map.set(handle, {
    handle,
    title: title || current.title,
    url: url || current.url,
    hasPosts: Boolean(hasPosts || current.hasPosts),
  });
}

async function blogsFromGraphql() {
  const shop = window.Shopify?.shop;
  const endpoints = [`${window.location.origin}/api/2025-01/graphql.json`];
  if (shop) {
    const domain = shop.includes('.') ? shop : `${shop}.myshopify.com`;
    endpoints.push(`https://${domain}/api/2025-01/graphql.json`);
  }

  const results = await Promise.all(
    endpoints.map((endpoint) =>
      fetchJson(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: BLOGS_QUERY }),
      })
    )
  );

  for (const data of results) {
    const nodes = data?.data?.blogs?.nodes || data?.data?.blogs?.edges?.map((edge) => edge.node);
    if (!Array.isArray(nodes) || !nodes.length) continue;
    return nodes
      .filter((node) => node?.handle)
      .map((node) => ({
        handle: node.handle,
        title: node.title,
        url: node.onlineStoreUrl || `/blogs/${encodeURIComponent(node.handle)}`,
        hasPosts: false,
      }));
  }
  return [];
}

async function handlesFromSitemap() {
  const handles = new Set();
  const indexXml = await fetchText(`${window.location.origin}/sitemap.xml`);
  const sitemapUrls = parseLocs(indexXml)
    .filter((loc) => /sitemap_blogs/i.test(loc))
    .map(toSameOrigin);
  if (!sitemapUrls.length) sitemapUrls.push(`${window.location.origin}/sitemap_blogs_1.xml`);

  const bodies = await Promise.all(sitemapUrls.map((url) => fetchText(url)));
  for (const xml of bodies) {
    for (const loc of parseLocs(xml)) {
      const handle = blogPathHandle(loc);
      if (handle) handles.add(handle);
    }
  }
  return [...handles];
}

class BlogTypeNav extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  async #load() {
    const list = this.querySelector('[data-blog-type-list]');
    if (!list) return;

    const currentHandle = (this.dataset.currentHandle || blogPathHandle(window.location.pathname)).toLowerCase();
    const shopHandle = this.dataset.shopHandle || '';
    const overrides = this.#overrides();
    const byHandle = new Map();

    const [graphqlBlogs, sitemapHandles] = await Promise.all([
      blogsFromGraphql(),
      handlesFromSitemap(),
    ]);

    for (const item of graphqlBlogs) {
      upsert(byHandle, item);
    }
    for (const handle of sitemapHandles) {
      upsert(byHandle, { handle, hasPosts: true });
    }

    const records = [...byHandle.values()].filter((item) => !isBrandBlog(item.handle, item.title, shopHandle));
    const withPosts = records.filter((item) => item.hasPosts);
    const types = withPosts.length ? withPosts : records;

    const existing = new Set(
      [...list.querySelectorAll('[data-blog-handle]')].map((item) => (item.dataset.blogHandle || '').toLowerCase())
    );

    for (const item of types) {
      const handleKey = item.handle.toLowerCase();
      const title = item.title || overrides[item.handle]?.title || titleFromHandle(item.handle);
      const url = item.url || `/blogs/${encodeURIComponent(item.handle)}`;

      if (existing.has(handleKey)) {
        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(item.handle) : item.handle;
        const label = list.querySelector(`[data-blog-handle="${escaped}"] .blog-categories__label`);
        if (label && title) label.textContent = title;
        continue;
      }

      const override = overrides[item.handle] || {};
      list.append(
        createTypePill({
          handle: item.handle,
          title,
          url,
          active: handleKey === currentHandle,
          iconHtml: override.icon
            ? `<img src="${String(override.icon).replace(/"/g, '&quot;')}" alt="" width="16" height="16" class="blog-categories__icon-image">`
            : '',
        })
      );
      existing.add(handleKey);
    }
  }

  #overrides() {
    const script = this.querySelector('[data-blog-type-overrides]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }
}

if (!customElements.get('blog-type-nav')) {
  customElements.define('blog-type-nav', BlogTypeNav);
}
